import React from 'react';
import { logger } from './utils/logger';

if (import.meta.env.DEV) {
  logger.debug('ENTRY', undefined, 'main');
}

/**
 * ✅ STEP 2.4: Sentry Error Tracking Integration
 * 
 * Enabled: Error tracking for production debugging
 * 
 * Configuration:
 * - Set VITE_SENTRY_DSN in environment variables (production only)
 * - Optionally set VITE_GIT_SHA for release tracking
 * - Only activates when VITE_SENTRY_DSN is provided in production builds
 */
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { InitialDataProvider, InitialSnapshot } from './contexts/InitialDataContext';

import { initRUM, initLCPDebug } from './utils/rum';
import { setupGlobalErrorHandling } from './utils/errorHandling';
import { initProductAnalytics } from './utils/productAnalytics';

initRUM();
initLCPDebug();
initProductAnalytics();
setupGlobalErrorHandling();

// Initialize theme (light/dark) with persistence and system fallback
(function initTheme() {
  try {
    const root = document.documentElement;
    const stored = localStorage.getItem('theme'); // 'light' | 'dark'
    const mql = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    const prefersDark = mql ? mql.matches : false;
    const theme = stored === 'light' || stored === 'dark' ? stored : (prefersDark ? 'dark' : 'light');
    root.setAttribute('data-theme', theme);
    
    // ✅ FIX: Listen for system preference changes (only if no stored preference)
    // This allows the theme to update when user changes OS system preference
    if (!stored && mql && mql.addEventListener) {
      mql.addEventListener('change', (e) => {
        const newTheme = e.matches ? 'dark' : 'light';
        root.setAttribute('data-theme', newTheme);
      });
    }
  } catch (error) {
    // Theme init failure is non-critical, but log in dev
    if (import.meta.env.DEV) {
      logger.debug('Theme initialization failed (non-critical)', { error }, 'main');
    }
  }
})();

// ✅ STEP 2.4: Sentry initialization (only in production with DSN)
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    release: import.meta.env.VITE_GIT_SHA || undefined,
    environment: import.meta.env.MODE || 'production',
    // Only capture errors, not all console logs
    beforeSend(event, hint) {
      // Filter out non-critical errors
      if (event.exception) {
        const error = hint.originalException;
        // Don't send network errors (handled by retry logic)
        if (error instanceof Error && (
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('timeout')
        )) {
          return null; // Don't send to Sentry
        }
      }
      return event;
    }
  });
  
  if (import.meta.env.DEV) {
    logger.debug('Sentry initialized for production', { 
      hasDsn: !!import.meta.env.VITE_SENTRY_DSN 
    }, 'main');
  }
} else if (import.meta.env.PROD) {
  // Production but no DSN - expected if Sentry not configured, suppress warning
  // Sentry is optional - app works fine without it
}

/**
 * Reads edge-injected snapshot data from DOM script tag
 * This allows the edge function to inject initial data for faster loading
 * Returns null if snapshot is missing or invalid
 */
function readInitialSnapshot(): InitialSnapshot | null {
  try {
    const script = document.getElementById('__UPDATES_SNAPSHOT__');
    if (!script || !script.textContent) {
      // Edge-injected snapshot is optional - app falls back to live fetch
      // This is expected behavior, not an error
      if (import.meta.env.DEV) {
        logger.debug('No edge-injected snapshot found; will use live fetch', undefined, 'main');
      }
      return null;
    }
    
    const data = JSON.parse(script.textContent);
    
    if (!data || !data.week_key || !data.funnel || !Array.isArray(data.creators)) {
      logger.warn('Invalid snapshot structure', { data }, 'main');
      return null;
    }
    
    if (import.meta.env.DEV) {
      logger.debug('Snapshot loaded', { week_key: data.week_key }, 'main');
    }
    return data;
  } catch (error) {
    logger.error('Failed to parse initial snapshot', { error }, 'main');
    return null;
  }
}

const initialUpdates = readInitialSnapshot();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <InitialDataProvider data={initialUpdates}>
      <App />
    </InitialDataProvider>
  </React.StrictMode>,
);
